<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="baseView.mode"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    :selectedIndex="undefined"
  >
    <template #headerContent>
      <GcHeaderContentStatic title="Email & Print Configuration" />
    </template>

    <div class="split-view-template css-v2 split-view">
      <gc-split-view
        :showLeftContainer="showLeftContainer"
        :showRightContainer="showRightContainer"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <GcDataGrid
              :dataSourceConfig="gridDataSourceConfig"
              :columns="gridColumns"
              :gridToolbar="gridToolbar"
              :rowClick="rowClickCallBack"
              :id="'split-view-grid'"
              v-if="dataLoaded"
              :heightPadding="20"
              :paging="false"
              ref="split-view-grid"
            />
          </div>
        </template>

        <template #rightContent>
          <gc-form-header-toolbar
            :toolbar-options="formConfig.headerToolbar"
            :data-model="formModel"
            :backRoute="'/email-print-configuration'"
            :formLocation="formConfig.headerToolbar.toolbarFormLocation"
            v-if="renderForm"
            :itemSaveCallBack="itemSaveCallBack"
            :baseView="baseView"
          ></gc-form-header-toolbar>

          <div class="view-card responsive-paddings split-view-form">
            <GcForm
              :formOptions="formOptions"
              :formItems="formConfig.formItems"
              :isNormal="true"
              :data-model="formModel"
              v-if="renderForm"
              :formMode="baseView.mode"
              :valueChangeCallBack="valueChangeCallBack"
              :checkValid="isValid"
              :validationGroup="'email-print-configuration-form'"
              :formLocation="'normal'"
              :formContentReady="formContentReady"
              ref="split-view-form"
              :formHeight="375"
            />
          </div>
        </template>
      </gc-split-view>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import { EmailPrintConfigurationService } from "../../../_services/api/settings/email-print-configuration.service";

// 2111 - Email & Print Configuration (Tasks 77530/77531 rework). Split view - a
// read-only selection list (left) + single-record detail/edit form (right),
// matching prototype.html's actual list-body/detail-panel structure. Hand-built
// from the same primitives templates/split-view.vue itself uses (gc-split-view,
// GcDataGrid, gc-form-header-toolbar, GcForm, RouteMixin, FormMixin) rather than
// that template component directly - its save flow is hard-coded to a generic
// POST-create/PATCH-by-key REST convention (SplitViewService.saveItem/updateItem)
// that can't reach POST api/Save_ScreenNameToLayout's custom {Changes:[...]}
// batch envelope. No row-add affordance anywhere (BR-09/AD-01 - edit-only over
// the existing pre-seeded set).
export default {
  mixins: [RouteMixin, FormMixin],

  data() {
    return {
      dataLoaded: true,
      renderForm: false,
      isValid: false,
      formOptions: { gcLabelLocation: "left" },
      formModel: {},
      availableReports: [],
      emailTemplates: [],

      gridDataSourceConfig: {
        endpoint: "ScreenNameToLayout",
        filter: "",
        serverPaging: true,
        allowGrouping: false,
        sort: [
          { selector: "ScreenName", asc: true },
          { selector: "ReportDisplayName", asc: true },
        ],
      },

      gridToolbar: {
        search: true,
        columnChooser: false,
        export: false,
        openFilter: true,
        expand: false,
        customButtons: [],
      },

      gridColumns: [
        {
          screenField: "ScreenName",
          label: "Screen Name",
          allowHiding: false,
          sortOrder: "asc",
          sortIndex: 0,
          width: 200,
          forceWidth: true,
          filterIndex: 0,
        },
        {
          screenField: "ReportDisplayName",
          label: "Report Name",
          filterIndex: 1,
        },
        // Prototype (.ybadge, prototype.html:89-90) renders these as a
        // literal "Y"/"N" pill (light green/grey), not a checkbox -
        // cellTemplate: "StatusYN" (gc-status-chip-y-n.vue, added for this
        // screen, wired into gc-data-grid.vue alongside the existing
        // Status/StatusYesNo named templates) matches it exactly. It reads
        // the raw "Y"/"N" string straight off dataField, so no
        // dataType: "boolean"/calculateCellValue mapping is needed.
        {
          screenField: "CheckInclude",
          headerCellTemplate: undefined,
          dataField: "AvailableForPDFPreview",
          label: "PDF Preview",
          alignment: "center",
          cellTemplate: "StatusYN",
          allowSorting: false,
          width: 100,
          filterIndex: 2,
          filterLabel: "PDF Preview",
          filterDataField: "AvailableForPDFPreview",
          dataSourceConfig: {
            data: [
              {
                text: "Yes",
                value: "Y",
              },
              {
                text: "No",
                value: "N",
              },
            ],
          },
          displayExpr: "text",
          valueExpr: "value",
        },
        {
          dataField: "AvailableForAutoEmail",
          label: "Automated Email",
          alignment: "center",
          cellTemplate: "StatusYN",
          allowSorting: false,
          width: 100,
          screenField: "CheckInclude",
          headerCellTemplate: undefined,
          filterIndex: 3,
          filterLabel: "Automated Email",
          filterDataField: "AvailableForAutoEmail",
          dataSourceConfig: {
            data: [
              {
                text: "Yes",
                value: "Y",
              },
              {
                text: "No",
                value: "N",
              },
            ],
          },
          displayExpr: "text",
          valueExpr: "value",
        },
        {
          dataField: "AvailableForEmail",
          label: "Prompted Email",
          alignment: "center",
          cellTemplate: "StatusYN",
          allowSorting: false,
          width: 100,
          screenField: "CheckInclude",
          headerCellTemplate: undefined,
          filterIndex: 3,
          filterLabel: "Prompted Email",
          filterDataField: "AvailableForEmail",
          dataSourceConfig: {
            data: [
              {
                text: "Yes",
                value: "Y",
              },
              {
                text: "No",
                value: "N",
              },
            ],
          },
          displayExpr: "text",
          valueExpr: "value",
        },
        {
          dataField: "AvailableForAutoPrint",
          label: "Automated Print",
          alignment: "center",
          cellTemplate: "StatusYN",
          allowSorting: false,
          width: 100,
          screenField: "CheckInclude",
          headerCellTemplate: undefined,
          filterIndex: 4,
          filterLabel: "Automated Print",
          filterDataField: "AvailableForAutoPrint",
          dataSourceConfig: {
            data: [
              {
                text: "Yes",
                value: "Y",
              },
              {
                text: "No",
                value: "N",
              },
            ],
          },
          displayExpr: "text",
          valueExpr: "value",
        },
        {
          dataField: "AvailableForPrint",
          label: "Prompted Print",
          alignment: "center",
          cellTemplate: "StatusYN",
          allowSorting: false,
          width: 100,
          screenField: "CheckInclude",
          headerCellTemplate: undefined,
          filterIndex: 5,
          filterLabel: "Prompted Print",
          filterDataField: "AvailableForPrint",
          dataSourceConfig: {
            data: [
              {
                text: "Yes",
                value: "Y",
              },
              {
                text: "No",
                value: "N",
              },
            ],
          },
          displayExpr: "text",
          valueExpr: "value",
        },
      ],
    };
  },

  computed: {
    showLeftContainer() {
      return !(this.isXSmall && this.baseView.mode !== "");
    },
    showRightContainer() {
      return !(this.baseView.mode === "review" && this.baseView.id === "");
    },
    rowHasEmailOn() {
      return (
        this.formModel.AvailableForEmailBool ||
        this.formModel.AvailableForAutoEmailBool
      );
    },
    formConfig() {
      return {
        headerToolbar: {
          closeSplit: true,
          audit: true,
          save: true,
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    // // disabled: true,
                  },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    // disabled: true,
                  },
                },
              ],
            },
          ],
          toolbarFormItems: [],
          toolbarFormLocation: "after",
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 2,
            items: [
              {
                dataField: "ScreenName",
                label: { text: "Screen Name" },
                allowUpdate: false,
              },
              {
                // Bound to formModel.ReportSelectionKey - a synthetic per-item
                // index into this.availableReports, NOT the bare
                // ReportDisplayName. ScreenNameToLayoutAvailableReports.sql
                // does `SELECT DISTINCT ScreenLayoutApplicationFeatureCode,
                // ReportDisplayName` - the *pair* is the only unique key,
                // ReportDisplayName alone is not (the same display name can
                // recur under a different Code). Selecting by name alone via
                // .find() would silently resolve duplicates to whichever
                // matching row happens to come first, not the one the admin
                // actually clicked. technical-design.md's Item 11 already
                // calls for "a composite value carrying both
                // ReportDisplayName and ScreenLayoutApplicationFeatureCode"
                // for this exact reason - this key is that composite.
                dataField: "ReportSelectionKey",
                label: { text: "Report Name" },
                editorType: "dxSelectBox",
                allowUpdate: this.baseView.mode !== "review",
                editorOptions: {
                  dataSource: this.availableReports,
                  valueExpr: "_key",
                  displayExpr: (item) =>
                    item
                      ? `${item.ReportDisplayName} (${item.ScreenLayoutApplicationFeatureCode})`
                      : "",
                  searchEnabled: true,
                  showClearButton: false,
                  // AD-04 revised/§9 Use Case 6 - selecting an option writes both
                  // ReportDisplayName and ScreenLayoutApplicationFeatureCode
                  // together, never independently (the prototype's single-string
                  // model is a confirmed defect, not something to repeat here).
                  onValueChanged: (e) => {
                    if (!e.event) return;
                    const item = (this.availableReports || []).find(
                      (r) => r._key === e.value,
                    );
                    if (item) {
                      this.formModel.ReportDisplayName = item.ReportDisplayName;
                      this.formModel.ScreenLayoutApplicationFeatureCode =
                        item.ScreenLayoutApplicationFeatureCode;
                    }
                  },
                  dropDownOptions: {
                    width: 300,
                  },
                },
              },
            ],
          },
          {
            itemType: "group",
            caption: "Method",
            // colCount:5 (one row of 5 switches) overflowed the 50%-width
            // split panel - every other split-view detail form in this
            // codebase (branch.vue, customer-type.vue, etc.) caps at
            // colCount:1 for exactly this reason. 2 keeps these 5 short
            // switches from doing the same while still fitting comfortably.
            colCount: 2,
            items: [
              {
                dataField: "AvailableForPDFPreviewBool",
                label: { text: "PDF Preview" },
                editorType: "dxSwitch",
                allowUpdate: this.baseView.mode !== "review",
              },
              {
                dataField: "AvailableForAutoEmailBool",
                label: { text: "Automated Email" },
                editorType: "dxSwitch",
                allowUpdate: this.baseView.mode !== "review",
              },
              {
                dataField: "AvailableForEmailBool",
                label: { text: "Prompted Email" },
                editorType: "dxSwitch",
                allowUpdate: this.baseView.mode !== "review",
              },
              {
                dataField: "AvailableForAutoPrintBool",
                label: { text: "Automated Print" },
                editorType: "dxSwitch",
                allowUpdate: this.baseView.mode !== "review",
              },
              {
                dataField: "AvailableForPrintBool",
                label: { text: "Prompted Print" },
                editorType: "dxSwitch",
                allowUpdate: this.baseView.mode !== "review",
              },
            ],
          },
          {
            itemType: "group",
            caption: "Email Details",
            colCount: 2,
            items: [
              // Reply To/CC/BCC editable only when the row's email flags allow.
              // The exact enable condition isn't stated as a formula anywhere in
              // technical-design.md §6 (only "editable only when a row's email
              // flags allow") - this OR condition is an inference corroborated
              // across BR-2111-01, the acceptance-criteria wording, and
              // prototype.html's own validateSave()
              // ((draft.autoEmail || draft.interactiveEmail)).
              {
                dataField: "ReplyTo",
                label: { text: "Reply To" },
                allowUpdate:
                  this.baseView.mode !== "review" && this.rowHasEmailOn,
                validationRules: [
                  {
                    type: "custom",
                    message:
                      "Enter one or more valid email addresses, separated by ';'.",
                    validationCallback: (e) =>
                      this.validateEmailListField(e.value),
                  },
                ],
              },
              {
                dataField: "EmailToCC",
                label: { text: "CC" },
                allowUpdate:
                  this.baseView.mode !== "review" && this.rowHasEmailOn,
                validationRules: [
                  {
                    type: "custom",
                    message:
                      "Enter one or more valid email addresses, separated by ';'.",
                    validationCallback: (e) =>
                      this.validateEmailListField(e.value),
                  },
                ],
              },
              {
                dataField: "EmailToBCC",
                label: { text: "BCC" },
                allowUpdate:
                  this.baseView.mode !== "review" && this.rowHasEmailOn,
                validationRules: [
                  {
                    type: "custom",
                    message:
                      "Enter one or more valid email addresses, separated by ';'.",
                    validationCallback: (e) =>
                      this.validateEmailListField(e.value),
                  },
                ],
              },
              {
                dataField: "EmailTemplateRecordID",
                label: { text: "Email Template" },
                editorType: "dxSelectBox",
                allowUpdate: this.baseView.mode !== "review",
                editorOptions: {
                  dataSource: this.emailTemplates,
                  valueExpr: "RecordId",
                  displayExpr: "Name",
                  showClearButton: true,
                  searchEnabled: true,
                  dropDownOptions: {
                    width: 300,
                  },
                },
                // BR-2111-01 client-side echo (§6): a row with an email flag on
                // and no template shows the standard required-field red border
                // rather than allowing an invalid save attempt.
                validationRules: [
                  {
                    type: "custom",
                    message:
                      "Email Template is required when Prompted Email or Automated Email is enabled.",
                    validationCallback: (e) =>
                      !(this.rowHasEmailOn && !e.value),
                  },
                ],
              },
            ],
          },
        ],
      };
    },
  },

  methods: {
    rowClickCallBack(e) {
      if (!e.data) return;
      this.onRowClicked(e, {
        view: this,
        page: "email-print-configuration",
        mode: "review",
        idProperty: "RecordID",
        idMode: "Guid",
      });
    },

    itemSaveCallBack() {
      this.isValid = true;
    },

    // Same single-address regex convention as
    // gc-popup-interactive-email.vue's isValidEmail.
    isValidEmailAddress(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },

    // Reply To/CC/BCC are semicolon-delimited multi-recipient strings, not a
    // single address - confirmed against how they're actually consumed
    // downstream (AutoEmailReportByReport.sql/AutoEmailReport.sql/
    // SendInteractiveAutoEmail.sql all split/replace on ';'), so DevExtreme's
    // built-in type:"email" rule (this app's normal single-address
    // convention, e.g. user.vue/company.vue) would wrongly reject real
    // multi-recipient values here. Those same SPs also substitute a literal
    // "(@useremail)" placeholder token at send time, so it's accepted as a
    // valid entry too rather than rejecting existing rows that already use
    // it. An empty value is valid - this only checks format, not presence.
    validateEmailListField(value) {
      if (!value) return true;
      const parts = value
        .split(";")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
      return parts.every(
        (part) => part === "(@useremail)" || this.isValidEmailAddress(part),
      );
    },

    // Default focus into Report Name on entering edit mode (button click or
    // a direct/refreshed load on the edit route) - same formContentReady +
    // mode-check idiom as product-location.vue/category-labels.vue.
    formContentReady(e) {
      if (this.baseView.mode === "edit" && e && e.component) {
        setTimeout(() => {
          const editor = e.component.getEditor("ReportSelectionKey");
          if (editor) editor.focus();
        }, 100);
      }
    },

    // Y/N <-> boolean mirror, same idiom templates/split-view.vue itself uses
    // for Inactive/ManufactureFlagBool.
    applyBoolMirrorsFromApi(row) {
      row.AvailableForPDFPreviewBool = row.AvailableForPDFPreview === "Y";
      row.AvailableForEmailBool = row.AvailableForEmail === "Y";
      row.AvailableForPrintBool = row.AvailableForPrint === "Y";
      row.AvailableForAutoEmailBool = row.AvailableForAutoEmail === "Y";
      row.AvailableForAutoPrintBool = row.AvailableForAutoPrint === "Y";
      return row;
    },

    buildChangeFromFormModel() {
      const m = this.formModel;
      return {
        RecordID: m.RecordID,
        ScreenLayoutApplicationFeatureCode:
          m.ScreenLayoutApplicationFeatureCode,
        ReportDisplayName: m.ReportDisplayName,
        AvailableForPDFPreview: m.AvailableForPDFPreviewBool ? "Y" : "N",
        AvailableForEmail: m.AvailableForEmailBool ? "Y" : "N",
        AvailableForPrint: m.AvailableForPrintBool ? "Y" : "N",
        AvailableForAutoEmail: m.AvailableForAutoEmailBool ? "Y" : "N",
        AvailableForAutoPrint: m.AvailableForAutoPrintBool ? "Y" : "N",
        ReplyTo: m.ReplyTo,
        EmailToCC: m.EmailToCC,
        EmailToBCC: m.EmailToBCC,
        EmailTemplateRecordID: m.EmailTemplateRecordID,
      };
    },

    async valueChangeCallBack(isValid, brokenRules) {
      if (!isValid) {
        this.displayValidationSummary(brokenRules);
        this.isValid = false;
        return;
      }

      this.showLoading(true);
      try {
        await EmailPrintConfigurationService.save([
          this.buildChangeFromFormModel(),
        ]);
        this.$notify("Configuration saved successfully", "success");
        this.setIgnoreRouteChangeConfirmation();
        this.$router.push({
          path: "/email-print-configuration/review/" + this.formModel.RecordID,
        });
      } catch (error) {
        // Server-side rejection surfacing (e.g. a BR-2111-02 duplicate-PK race
        // that slipped past client-side validation) isn't specified anywhere in
        // technical-design.md §6/§7/§9/§11 - reusing this app's existing
        // displayServerError convention as the pragmatic default.
        this.displayServerError(error, "Save Failed");
      } finally {
        this.showLoading(false);
        this.isValid = false;
      }
    },

    async getRecord() {
      this.showLoading(true);
      this.availableReports = [];
      try {
        const response = await EmailPrintConfigurationService.getByRecordId(
          this.baseView.id,
        );
        const row = response && response.length ? response[0] : null;
        if (row) {
          this.formModel = this.applyBoolMirrorsFromApi({ ...row });
          const availableReports =
            await EmailPrintConfigurationService.getAvailableReports(
              row.ScreenName,
            );

          // Bug 77915: IsLikelyMatch is a sort/highlight hint only, per
          // technical-design.md AD-04/§5 ("Never returns an empty list...
          // used only for the sort hint, not a hard filter") - it must
          // never be filtered out. A screen with sparse
          // ScreenReportParameters coverage (e.g. Work Orders) legitimately
          // returns every row as IsLikelyMatch: false, and a hard filter
          // there emptied the dropdown entirely and, since the "currently
          // assigned" lookup below only searches this same array, also hid
          // the saved value's own label in both Edit and Review mode.
          this.availableReports = availableReports
            .slice()
            .sort((a, b) => (b.IsLikelyMatch ? 1 : 0) - (a.IsLikelyMatch ? 1 : 0))
            .map((report, idx) => ({ ...report, _key: idx }));

          // Initialize the select box to the row's *currently assigned*
          // pair, matched on both fields together (not ReportDisplayName
          // alone - see the formItems comment on ReportSelectionKey).
          const currentMatch = this.availableReports.find(
            (r) =>
              r.ScreenLayoutApplicationFeatureCode ===
                row.ScreenLayoutApplicationFeatureCode &&
              r.ReportDisplayName === row.ReportDisplayName,
          );
          this.formModel.ReportSelectionKey = currentMatch
            ? currentMatch._key
            : null;
        }
      } catch (error) {
        this.displayServerError(
          error,
          "Failed to load Email & Print Configuration",
        );
      } finally {
        this.showLoading(false);

        this.renderForm = true;
      }
    },

    async handleLocalRouteChange() {
      this.renderForm = false;
      if (
        (this.baseView.mode === "review" || this.baseView.mode === "edit") &&
        this.baseView.id
      ) {
        await this.getRecord();
      } else {
        this.formModel = {};
        this.showLoading(false);
      }
      if (this.$refs["split-view-grid"])
        this.$refs["split-view-grid"].reSizeGrid();
    },

    backButtonFunction() {
      this.$router.push({ path: "/settings/system" });
    },
  },

  async created() {
    this.emailTemplates =
      await EmailPrintConfigurationService.getEmailTemplates();
  },

  watch: {
    $route: {
      immediate: true,
      handler: "handleLocalRouteChange",
    },
  },
};
</script>
